import { useForm, Controller } from 'react-hook-form';
import Body from 'storybookConfig/stories/molecules/Body';
import PrimaryButton from 'storybookConfig/stories/molecules/Button/PrimaryButton';
import Heading from 'storybookConfig/stories/molecules/Heading';
import Input from 'storybookConfig/stories/molecules/Input';
import Form from 'storybookConfig/stories/organisms/Form';
import styled from '@emotion/styled';
import Icon from 'storybookConfig/stories/molecules/Icon';
import Flex from '@react-css/flex';
import { useState } from 'react';
import { updateCompany } from 'utils/api/companies';
import useAlertQueue from 'hooks/useAlertQueue';
import { useMutation } from '@tanstack/react-query';
import type { Company } from 'types/models/company';
import { uploadPublicImage } from 'utils/api/documents';
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import { useAppSelector, useAppDispatch } from 'store/index';
import { selectCompanyId } from 'store/selectors/me/company';
import { updateMarketplaceOnboarding } from 'store/slices/me';
import type { FormProps, MarketplaceFormData } from './types';
import useSegment from 'hooks/useSegment';
import InfoTooltip from 'components/Common/InfoTooltip';

type FormFields = Pick<MarketplaceFormData, 'primaryContact' | 'secondaryContact'>;

const ChangeImageLabel = styled.label`
  width: 100%;
  cursor: pointer;
`;

const ChangeImageButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 32px;
  border: 1px solid ${({ theme }) => theme.color.blue500};
  color: ${({ theme }) => theme.color.blue500};
  border-radius: 23px;
  cursor: pointer;
`;

const UploadImageLabel = styled.label`
  width: 100%;
  cursor: pointer;
`;

const UploadImageInput = styled.input`
  display: none;
`;

const UploadImageContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100px;
  border: 1px dashed;
  border-color: ${({ theme }) => theme.color.gray400};
  border-radius: 10px;
  background-color: ${({ theme }) => theme.color.gray100};
  cursor: pointer;
`;

const UploadedImageContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100px;
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.color.blue500};
`;

const UploadedImage = styled.img`
  width: 65px;
  height: 65px;
  border-radius: 8px;
  object-fit: cover;
  object-position: center;
`;

const StyledPhoneInput = styled.div`
  width: 100%;

  .react-international-phone-input-container {
    background: ${({ theme }) => theme.color.gray100};
    border-radius: 23px;
    padding: 0 16px;
    min-height: 46px;
    display: flex;
    align-items: center;
    border: 1px solid transparent;
    width: 100%;

    &:focus-within {
      border-color: ${({ theme }) => theme.color.gray300};
    }

    .react-international-phone-country-selector-button {
      background: transparent;
      border: none;
      padding: 0 8px 0 0;
      display: flex;
      align-items: center;
      gap: 4px;
    }

    .react-international-phone-input {
      background: transparent;
      border: none;
      padding: 0;
      width: 100%;
      outline: none;
      color: ${({ theme }) => theme.color.bodyTextPrimary};
      font-size: 16px;

      &::placeholder {
        color: ${({ theme }) => theme.color.bodyTextSecondary};
      }
    }
  }

  &.invalid .react-international-phone-input-container {
    border-color: ${({ theme }) => theme.color.error500};
  }
`;

const ContactForm = ({ onNext, marketplaceData, setMarketplaceData }: FormProps) => {
  const { addErrorAlert, addSuccessAlert } = useAlertQueue();
  const dispatch = useAppDispatch();
  const [primaryPhoto, setPrimaryPhoto] = useState<string | undefined>(
    marketplaceData?.primaryContact?.profile
  );
  const [secondaryPhoto, setSecondaryPhoto] = useState<string | undefined>(
    marketplaceData?.secondaryContact?.profile
  );
  const companyId = useAppSelector(selectCompanyId);
  const { handleSubmit, register, control, formState } = useForm<FormFields>({
    mode: 'onBlur',
    values: marketplaceData,
  });
  const analytics = useSegment();

  const onLogoUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
    type: 'primary' | 'secondary'
  ) => {
    if (!event.target.files) return;

    try {
      const file = event.target.files[0];
      if (file) {
        const maxSize = 5 * 1024 * 1024; // 5MB in bytes
        if (file.size > maxSize) {
          addErrorAlert('File too large', 'Please select an image smaller than 5MB');
          return;
        }

        await uploadPublicImage(file, {
          onPreview: (url) => {
            if (type === 'primary') {
              setPrimaryPhoto(url);
              setMarketplaceData({
                primaryContact: {
                  ...marketplaceData?.primaryContact,
                  profile: url,
                },
              });
            } else {
              setSecondaryPhoto(url);
              setMarketplaceData({
                secondaryContact: {
                  ...marketplaceData?.secondaryContact,
                  profile: url,
                },
              });
            }
          },
          onComplete: (url) => {
            if (type === 'primary') {
              setPrimaryPhoto(url);
              setMarketplaceData({
                primaryContact: {
                  ...marketplaceData?.primaryContact,
                  profile: url,
                },
              });
            } else {
              setSecondaryPhoto(url);
              setMarketplaceData({
                secondaryContact: {
                  ...marketplaceData?.secondaryContact,
                  profile: url,
                },
              });
            }
          },
          onError: (error) => {
            console.error('Error uploading image', error);
            addErrorAlert('Something went wrong', 'Unable to upload image');
          },
        });
      }
    } catch (error) {
      console.error('Error uploading image', error);
      addErrorAlert('Something went wrong', 'Unable to upload image');
    }
  };

  const { mutate: updateContactInfo, isLoading } = useMutation<Company, Error, FormFields>({
    mutationFn: (data: FormFields) => {
      const updatedData: MarketplaceFormData = {
        primaryContact: {
          ...data.primaryContact,
          profile: primaryPhoto || '',
        },
        secondaryContact: {
          ...data.secondaryContact,
          phoneNumber:
            data.secondaryContact?.phoneNumber && data.secondaryContact.phoneNumber.length < 4
              ? ''
              : data.secondaryContact?.phoneNumber,
          profile: secondaryPhoto || '',
        },
      };

      setMarketplaceData(updatedData);
      return updateCompany(companyId ?? '', {
        marketplaceSettings: updatedData,
      });
    },
    onSuccess: (data) => {
      analytics?.track('Supplier Onboarding Contact Info Completed', {
        primary_contact: {
          first_name: data.marketplaceSettings.primaryContact.firstName,
          last_name: data.marketplaceSettings.primaryContact.lastName,
          job_title: data.marketplaceSettings.primaryContact.jobTitle,
          email: data.marketplaceSettings.primaryContact.email,
          phone_number: data.marketplaceSettings.primaryContact.phoneNumber,
        },
        secondary_contact: {
          first_name: data.marketplaceSettings.secondaryContact.firstName,
          last_name: data.marketplaceSettings.secondaryContact.lastName,
          job_title: data.marketplaceSettings.secondaryContact.jobTitle,
          email: data.marketplaceSettings.secondaryContact.email,
          phone_number: data.marketplaceSettings.secondaryContact.phoneNumber,
        },
      });

      dispatch(updateMarketplaceOnboarding(data.marketplaceSettings));
      addSuccessAlert('Added Contact Info', 'Your contact has been saved.');
      if (onNext) {
        onNext();
      }
    },
    onError: (error) => {
      analytics?.track('Supplier Onboarding Step Error', {
        step_name: 'contact',
        error_message: error.message,
      });
      addErrorAlert('Something went wrong', error.message);
      console.error('Unable to update marketplace settings', error);
    },
  });

  const onSubmit = handleSubmit((data) => {
    updateContactInfo(data);
  });

  return (
    <Form onSubmit={onSubmit}>
      <Heading variant="Headings/H3">Contact Information</Heading>
      <Form.FieldGroup>
        <Body variant="Body/Header">Primary</Body>
        <Flex gap="16px">
          <Form.Field
            labelText="First Name"
            id="primary-contact-first-name"
            errorText={formState.errors.primaryContact?.firstName?.message}
            isRequired
          >
            <Input
              {...register('primaryContact.firstName', { required: 'First Name is required' })}
              placeholder="John"
              isFullWidth
            />
          </Form.Field>
          <Form.Field
            labelText="Last Name"
            id="primary-contact-last-name"
            errorText={formState.errors.primaryContact?.lastName?.message}
            isRequired
          >
            <Input
              {...register('primaryContact.lastName', { required: 'Last name is required' })}
              placeholder="Doe"
              isFullWidth
            />
          </Form.Field>
        </Flex>
        <Form.Field
          labelText="Job Title"
          id="primary-contact-job-title"
          errorText={formState.errors.primaryContact?.jobTitle?.message}
          isRequired
        >
          <Input
            {...register('primaryContact.jobTitle', { required: 'Job Title is required' })}
            placeholder="Senior Manager"
            isFullWidth
          />
        </Form.Field>
        <Form.Field
          labelText="Email"
          id="primary-contact-email"
          errorText={formState.errors.primaryContact?.email?.message}
          isRequired
        >
          <Input
            type="email"
            {...register('primaryContact.email', { required: 'Email is required' })}
            placeholder="email@example.com"
            isFullWidth
          />
        </Form.Field>
        <Form.Field
          labelText="Phone Number"
          id="primary-contact-phone"
          errorText={formState.errors.primaryContact?.phoneNumber?.message}
          isRequired
        >
          <StyledPhoneInput>
            <Controller
              name="primaryContact.phoneNumber"
              control={control}
              rules={{
                required: 'Phone number is required',
                validate: (value) => {
                  if (!value || value === '1') return true; // Allow default US country code
                  const numericValue = value.replace(/\D/g, '');
                  if (numericValue.length < 10) return 'Phone number is too short';
                  if (numericValue.length > 15) return 'Phone number is too long';
                  return true;
                },
              }}
              render={({ field: { onChange, value }, fieldState: { invalid } }) => (
                <StyledPhoneInput className={invalid ? 'invalid' : ''}>
                  <PhoneInput defaultCountry="us" value={value} onChange={onChange} />
                </StyledPhoneInput>
              )}
            />
          </StyledPhoneInput>
        </Form.Field>
        <Form.Field
          labelText={
            <Flex gap="4px" alignItemsCenter>
              Primary Contact Photo
              <InfoTooltip>Photo will be used between you and your partners.</InfoTooltip>
            </Flex>
          }
          id="primary-contact-photo"
        >
          <UploadImageInput
            type="file"
            id="primary-contact-logo-upload"
            onChange={(e) => onLogoUpload(e, 'primary')}
          />
          {primaryPhoto ? (
            <>
              <UploadedImageContainer>
                <UploadedImage src={primaryPhoto} alt="Primary Contact" />
              </UploadedImageContainer>
              <ChangeImageLabel htmlFor="primary-contact-logo-upload">
                <ChangeImageButton>Change Image</ChangeImageButton>
              </ChangeImageLabel>
            </>
          ) : (
            <UploadImageLabel htmlFor="primary-contact-logo-upload">
              <UploadImageContainer>
                <Flex column alignItemsCenter>
                  <Icon name="image" size="30px" color="gray400" />
                  <Body variant="Body/Body Small" color="gray400">
                    Upload Image
                  </Body>
                </Flex>
              </UploadImageContainer>
            </UploadImageLabel>
          )}
        </Form.Field>
      </Form.FieldGroup>
      <Form.FieldGroup>
        <Flex gap="4px" alignItemsCenter>
          <Body variant="Body/Header">Secondary</Body>
          <InfoTooltip>
            The Secondary Point of Contact serves as a backup option when we are unable to reach the
            Primary Point of Contact.
          </InfoTooltip>
        </Flex>
        <Flex gap="16px">
          <Form.Field
            labelText="First Name"
            id="secondary-contact-first-name"
            errorText={formState.errors.secondaryContact?.firstName?.message}
          >
            <Input {...register('secondaryContact.firstName')} placeholder="John" isFullWidth />
          </Form.Field>
          <Form.Field
            labelText="Last Name"
            id="secondary-contact-last-name"
            errorText={formState.errors.secondaryContact?.lastName?.message}
          >
            <Input {...register('secondaryContact.lastName')} placeholder="Doe" isFullWidth />
          </Form.Field>
        </Flex>
        <Form.Field
          labelText="Job Title"
          id="secondary-contact-job-title"
          errorText={formState.errors.secondaryContact?.jobTitle?.message}
        >
          <Input
            {...register('secondaryContact.jobTitle')}
            placeholder="Senior Manager"
            isFullWidth
          />
        </Form.Field>
        <Form.Field
          labelText="Email"
          id="secondary-contact-email"
          errorText={formState.errors.secondaryContact?.email?.message}
        >
          <Input
            type="email"
            {...register('secondaryContact.email')}
            placeholder="email@example.com"
            isFullWidth
          />
        </Form.Field>
        <Form.Field
          labelText="Phone Number"
          id="secondary-contact-phone"
          errorText={formState.errors.secondaryContact?.phoneNumber?.message}
        >
          <StyledPhoneInput>
            <Controller
              name="secondaryContact.phoneNumber"
              control={control}
              rules={{
                validate: (value) => {
                  if (!value) return true;
                  const numericValue = value.replace(/\D/g, '');
                  if (numericValue.length > 4 && numericValue.length < 10)
                    return 'Phone number is too short';
                  if (numericValue.length > 15) return 'Phone number is too long';
                  return true;
                },
              }}
              render={({ field: { onChange, value }, fieldState: { invalid } }) => (
                <StyledPhoneInput className={invalid ? 'invalid' : ''}>
                  <PhoneInput defaultCountry="us" value={value} onChange={onChange} />
                </StyledPhoneInput>
              )}
            />
          </StyledPhoneInput>
        </Form.Field>
        <Form.Field labelText="Secondary Contact Photo" id="secondary-contact-photo">
          <UploadImageInput
            type="file"
            id="secondary-contact-logo-upload"
            onChange={(e) => onLogoUpload(e, 'secondary')}
          />
          {secondaryPhoto ? (
            <>
              <UploadedImageContainer>
                <UploadedImage src={secondaryPhoto} alt="Secondary Contact" />
              </UploadedImageContainer>
              <ChangeImageLabel htmlFor="secondary-contact-logo-upload">
                <ChangeImageButton>Change Image</ChangeImageButton>
              </ChangeImageLabel>
            </>
          ) : (
            <UploadImageLabel htmlFor="secondary-contact-logo-upload">
              <UploadImageContainer>
                <Flex column alignItemsCenter>
                  <Icon name="image" size="30px" color="gray400" />
                  <Body variant="Body/Body Small" color="gray400">
                    Upload Image
                  </Body>
                </Flex>
              </UploadImageContainer>
            </UploadImageLabel>
          )}
        </Form.Field>
      </Form.FieldGroup>
      <Form.Footer column gap="16px">
        <PrimaryButton type="submit" disabled={isLoading}>
          {isLoading ? 'Saving...' : 'Next'}
        </PrimaryButton>
      </Form.Footer>
    </Form>
  );
};

export default ContactForm;
